import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RefreshCw, FileText } from "lucide-react";
import { apiFetch } from "@/utils/api";

// Visor de "Ver factura": pide el documento al backend y lo muestra embebido
// (PDF en iframe, imagen escaneada en <img>). Renderiza su propio botón
// disparador 👁 y carga el archivo al abrirse, vía blob. Solo lectura.
// Reutilizable: en Facturas Emitidas apunta al PDF generado; en Recibidas, al
// documento original (PDF adjunto o documento OCR). El tipo (PDF/imagen) se
// detecta por el Content-Type del blob.

export default function InvoicePreviewDialog({ url, title = "Vista previa", testid, open: openProp, onOpenChange, hideTrigger = false }) {
  const isControlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = isControlled ? openProp : openState;
  const setOpen = (v) => (isControlled ? onOpenChange?.(v) : setOpenState(v));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isImage, setIsImage] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiFetch(url);
      if (!res.ok) {
        setError(true);
        return;
      }
      const blob = await res.blob();
      setIsImage((blob.type || "").startsWith("image/"));
      setBlobUrl(URL.createObjectURL(blob));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const cleanup = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) load();
    else cleanup();
  };

  const handleTrigger = (e) => {
    e.stopPropagation();
    handleOpenChange(true);
  };

  // Modo controlado (prop `open`): el padre abre/cierra sin pasar por el
  // trigger interno; disparamos carga/limpieza según el valor externo.
  useEffect(() => {
    if (!isControlled) return;
    if (openProp) load();
    else cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProp, isControlled]);

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={handleTrigger}
          title="Ver documento"
          className="inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-blue-600 hover:bg-slate-100 transition-colors"
          data-testid={testid}
        >
          <FileText className="w-4 h-4" />
        </button>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="truncate">{title}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="min-h-[60vh] bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden">
            {loading ? (
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            ) : error ? (
              <p className="text-sm text-rose-600 p-4">
                No se pudo cargar la vista previa del documento.
              </p>
            ) : !blobUrl ? null : isImage ? (
              <img src={blobUrl} alt={title} className="max-h-[70vh] w-full object-contain" />
            ) : (
              <iframe src={blobUrl} title={title} className="w-full h-[70vh]" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
